import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Cloud,
  Database,
  Eye,
  Globe,
  Lock,
  Monitor,
  Plane,
  Search,
  Server,
  Star,
  Store,
  Terminal,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { apiGet, apiPost } from '../lib/api'
import { useSession } from '../context/SessionContext'
import { useToast } from '../context/ToastContext'
import { EmptyState, Skeleton } from '../components/ui'
import { cn } from '../lib/cn'

interface Resource {
  id: string
  name: string
  description: string
  type: 'WEB' | 'SSH' | 'RDP' | 'VNC'
  tags: string[]
  clipboard_allowed: boolean
  recording_enabled: boolean
  access_modes?: string[]
  native_rdp_allowed?: boolean
  deployment_type?: 'connector' | 'reverse_proxy' | 'external_launch'
  subdomain?: string
  icon_url?: string
  icon_glyph?: string
}

const sessionPath: Record<string, string> = {
  SSH: '/session/ssh',
  RDP: '/session/rdp',
  VNC: '/session/vnc',
  WEB: '/app',
}

// Icon color encodes protocol, full stop — every resource of the same type
// looks the same color at a glance, regardless of which app it is. This is
// the ONLY thing color communicates on a tile; app identity is the glyph
// (see GLYPH_ICONS) or a real uploaded logo (icon_url), layered on top.
const PROTOCOL_COLORS: Record<string, string> = {
  WEB: 'bg-blue-600',
  RDP: 'bg-violet-600',
  SSH: 'bg-red-600',
  VNC: 'bg-amber-600',
}

// Curated glyph set an admin can pick per resource (AdminResourcesPage's
// "Icon" field) to differentiate apps that share a protocol — mirrors
// ICON_GLYPHS in AdminResourcesPage.tsx and the DB CHECK constraint in
// saas/db/migrations/015_resource_icon_glyph.sql. Keep all three in sync.
const GLYPH_ICONS: Record<string, LucideIcon> = {
  server: Server,
  globe: Globe,
  plane: Plane,
  store: Store,
  users: Users,
  database: Database,
  terminal: Terminal,
  monitor: Monitor,
  eye: Eye,
  cloud: Cloud,
  zap: Zap,
  lock: Lock,
}

// When a resource has no icon_glyph set, fall back to one glyph per
// protocol — chosen to match what that protocol visually is (a monitor for
// RDP, a terminal for SSH, an eye for VNC's screen-viewing model), not
// stored server-side so changing the default later needs no backfill.
const DEFAULT_GLYPH_BY_TYPE: Record<string, LucideIcon> = {
  WEB: Globe,
  RDP: Monitor,
  SSH: Terminal,
  VNC: Eye,
}

const FAVORITES_KEY = 'idefy.catalog.favorites'
function loadFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}
function saveFavorites(ids: Set<string>) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify([...ids]))
}

async function downloadNativeRDP(resourceId: string) {
  const data = await apiPost<{ rdp_content: string }>('/api/v1/rdp/launch-native', {
    resource_id: resourceId,
  })
  const blob = new Blob([data.rdp_content], { type: 'application/rdp' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `idefy-${resourceId}.rdp`
  a.click()
  URL.revokeObjectURL(url)
}

export default function CatalogPage() {
  const { toast } = useToast()
  const { user } = useSession()
  const email = user?.email ?? ''
  const [resources, setResources] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'all' | 'favorites'>('all')
  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavorites())
  // ADR-011: reverse_proxy resources with a subdomain assigned must be
  // linked directly to {subdomain}.{resourceSubdomainBase}, not /app/{id} —
  // the path-prefixed route breaks backends emitting root-relative asset
  // paths (confirmed live against ng.colortokens.com). Empty until the
  // config fetch resolves; resources without a subdomain are unaffected.
  const [resourceSubdomainBase, setResourceSubdomainBase] = useState('')

  useEffect(() => {
    apiGet<Resource[]>('/api/v1/catalog')
      .then((data) => {
        setResources(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch((err) => {
        setError(`Catalog unavailable: ${err.message}`)
        setResources([])
        setLoading(false)
      })
    apiGet<{ resource_subdomain_base?: string }>('/api/v1/config')
      .then((data) => setResourceSubdomainBase(data.resource_subdomain_base || ''))
      .catch(() => setResourceSubdomainBase(''))
  }, [])

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      saveFavorites(next)
      return next
    })
  }

  const firstName = useMemo(() => {
    if (!email) return ''
    const local = email.split('@')[0]
    const first = local.split(/[._-]/)[0]
    return first.charAt(0).toUpperCase() + first.slice(1)
  }, [email])

  const searched = resources.filter((r) => {
    const q = search.toLowerCase()
    return (
      r.name.toLowerCase().includes(q) ||
      r.description?.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q)
    )
  })
  const visible = tab === 'favorites' ? searched.filter((r) => favorites.has(r.id)) : searched

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Welcome{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="mt-1 text-sm text-muted">Launch your apps with a single click.</p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            type="search"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      <div className="mb-6 flex items-center gap-6 border-b border-border text-sm">
        {(
          [
            ['all', `All Applications (${searched.length})`],
            ['favorites', `Favorites (${favorites.size})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'border-b-2 pb-3 font-medium transition-colors',
              tab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Box className="h-8 w-8" />}
          title={tab === 'favorites' ? 'No favorites yet' : 'No resources found'}
          description={
            tab === 'favorites'
              ? 'Star an app to pin it here.'
              : search
                ? 'Try a different search term'
                : 'Contact your administrator to request access'
          }
        />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
          {visible.map((resource) => (
            <AppTile
              key={resource.id}
              resource={resource}
              resourceSubdomainBase={resourceSubdomainBase}
              favorite={favorites.has(resource.id)}
              onToggleFavorite={() => toggleFavorite(resource.id)}
              onNativeLaunch={async (id) => {
                try {
                  await downloadNativeRDP(id)
                  toast({ title: 'RDP file downloaded', variant: 'success' })
                } catch (e) {
                  toast({
                    title: 'Native launch failed',
                    description: e instanceof Error ? e.message : 'Unknown error',
                    variant: 'danger',
                  })
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AppTile({
  resource,
  resourceSubdomainBase,
  favorite,
  onToggleFavorite,
  onNativeLaunch,
}: {
  resource: Resource
  resourceSubdomainBase: string
  favorite: boolean
  onToggleFavorite: () => void
  onNativeLaunch: (id: string) => void
}) {
  const [iconFailed, setIconFailed] = useState(false)
  const [chooserOpen, setChooserOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const path = sessionPath[resource.type] || '/app'
  // WEB resources with a subdomain assigned go straight to their subdomain
  // URL — /app/{id} path-prefixes the request, which breaks backends that
  // emit root-relative asset paths or issue absolute (non-prefixed) requests
  // of their own (see ADR-011). Originally reverse_proxy-only; connector
  // resources have the identical failure mode against the same class of
  // backend (confirmed live against RD Web Client through the on-prem
  // Connector) and subdomain routing works correctly for them too — the
  // connector-deployment fallback in subdomain.caddy.tmpl resolves the
  // resource ID from a header set by identity's subdomain lookup, not by
  // path-parsing, so it doesn't share /app/*'s path-prefix limitations.
  const subdomainHref =
    resource.type === 'WEB' &&
    (resource.deployment_type === 'reverse_proxy' || resource.deployment_type === 'connector') &&
    resource.subdomain &&
    resourceSubdomainBase
      ? `https://${resource.subdomain}.${resourceSubdomainBase}`
      : null
  const browserHref =
    subdomainHref ?? (resource.type === 'WEB' ? `/app/${resource.id}` : `${path}/${resource.id}`)
  const modes = resource.access_modes ?? ['browser']
  const showBrowser = modes.includes('browser')
  const showNative =
    resource.type === 'RDP' &&
    resource.native_rdp_allowed &&
    modes.includes('native') &&
    !resource.recording_enabled
  // Dual-mode RDP resources can't be a single <a>/<Link> for the whole card —
  // clicking opens a small chooser (Browser / RDP App) instead of navigating
  // straight through, since there are two valid destinations.
  const dualMode = showNative && showBrowser
  // Vocabulary is Public/Private everywhere, full stop — reverse_proxy and
  // external_launch are both reachable without a connector, so both read as
  // Public; only connector-backed resources are Private. This collapses the
  // old three-way On-prem/Direct-internet/External-launch split.
  const isPrivate = resource.deployment_type === 'connector'

  // Close the chooser on outside click / Escape, same pattern as any
  // lightweight popover in this app — no portal needed since the card is
  // small and the popover is anchored directly beneath it.
  useEffect(() => {
    if (!chooserOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setChooserOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChooserOpen(false)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [chooserOpen])

  // Fallback glyph: protocol color (never recolored by app identity) +
  // admin-picked category glyph, or a per-protocol default when unset. Real
  // uploaded logos (icon_url) are never recolored — they render as-is.
  const GlyphIcon = (resource.icon_glyph && GLYPH_ICONS[resource.icon_glyph]) || DEFAULT_GLYPH_BY_TYPE[resource.type] || Globe
  const fallbackAvatar = (
    <div
      className={cn(
        'flex h-[52px] w-[52px] items-center justify-center rounded-xl',
        PROTOCOL_COLORS[resource.type] ?? 'bg-slate-500',
      )}
    >
      <GlyphIcon className="h-6 w-6 text-white" />
    </div>
  )

  const icon =
    resource.icon_url && !iconFailed ? (
      <img
        src={resource.icon_url}
        alt=""
        className="h-[52px] w-[52px] rounded-xl border border-border bg-surface-muted object-cover"
        onError={() => setIconFailed(true)}
      />
    ) : (
      fallbackAvatar
    )

  // Connectivity is one glyph — globe for Public, lock for Private — instead
  // of a three-way icon split, since the vocabulary itself is now two-way.
  const ConnectivityIcon = isPrivate ? Lock : Globe
  const metaLine = (
    <div className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-muted">
      <ConnectivityIcon className="h-[11px] w-[11px]" />
      <span className="text-muted-foreground">{resource.type}</span>
      <span className="text-border-strong">·</span>
      {isPrivate ? 'Private' : 'Public'}
    </div>
  )

  const cardInner = (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onToggleFavorite()
        }}
        aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
        className="absolute right-2 top-2 z-[1] rounded p-0.5 text-muted opacity-0 transition-opacity hover:text-warning group-hover:opacity-100 group-focus-within:opacity-100"
      >
        <Star className={cn('h-4 w-4', favorite && 'fill-warning text-warning opacity-100')} />
      </button>

      <div className="my-3 flex justify-center">{icon}</div>
      <p className="line-clamp-1 text-center text-[13px] font-semibold text-foreground">
        {resource.name}
      </p>
      <div className="mt-0.5">{metaLine}</div>
    </>
  )

  const cardClass =
    'group relative block rounded-lg border border-border bg-surface px-3.5 py-4 text-left transition-colors hover:border-primary/40 hover:shadow-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary'

  if (dualMode) {
    return (
      <div
        ref={cardRef}
        className={cn(cardClass, 'cursor-pointer')}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={chooserOpen}
        onClick={() => setChooserOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setChooserOpen((v) => !v)
          }
        }}
      >
        {cardInner}

        {chooserOpen && (
          <div
            role="menu"
            className="absolute inset-x-2 top-full z-20 mt-1 flex flex-col gap-0.5 rounded-lg border border-border-strong bg-surface p-1.5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <Link
              to={browserHref}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-surface-muted"
              onClick={() => setChooserOpen(false)}
            >
              Open in Browser
            </Link>
            <div className="mx-1 my-0.5 h-px bg-border" />
            <button
              type="button"
              role="menuitem"
              className="rounded-md px-2.5 py-1.5 text-left text-xs font-semibold text-foreground hover:bg-surface-muted"
              onClick={() => {
                setChooserOpen(false)
                onNativeLaunch(resource.id)
              }}
            >
              Open RDP App
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!showBrowser) {
    // No valid launch mode — inert tile, matching today's behavior.
    return <div className={cardClass}>{cardInner}</div>
  }

  return resource.type === 'WEB' ? (
    <a href={browserHref} target="_blank" rel="noopener noreferrer" className={cardClass}>
      {cardInner}
    </a>
  ) : (
    <Link to={browserHref} target="_blank" rel="noopener noreferrer" className={cardClass}>
      {cardInner}
    </Link>
  )
}
